import React, { useState, useEffect, useRef } from 'react';
import { Chart } from '@domoinc/domo-phoenix';
import { CircularProgress, Paper, Box } from '@mui/material';
import ChartHeader from './chart_header';
import Graph from './graph';
import Audit from '../utils/audit_util';
import ChartFooter from './chart_footer';
import DrillFunctions from './drill_util';
import DateFunctions from './date_util';
import { dataCharts } from '../data/data_charts';

const Card = (props) => {
    const [data, setData] = useState([]);
    const [options, setOptions] = useState({
        height: 0,
        width: 0,
        animate: true
    });
    const [definition, setDefinition] = useState({
        display_name: "loading...",
        chart_type: "badge_vert_bar"
    });
    const [drillText, setDrillText] = useState('');
    const [updatedText, setUpdatedText] = useState('');
    const [showToolbar, setShowToolbar] = useState(false);
    const [showUpdate, setShowUpdate] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const chartRef = useRef(null);

    const loadChartPicker = () => {
        if (!chartRef.current) {
            console.warn('Chart not initialized');
            return;
        }
        
        try {
            if (!chartRef.current.isChartPickerOpen || !chartRef.current.showChartPicker || !chartRef.current.hideChartPicker) {
                console.warn('Chart picker methods not available');
                return;
            }

            if (chartRef.current.isChartPickerOpen()) {
                chartRef.current.hideChartPicker();
                Audit.LogUserAction('chart.hide.chart.picker', 'chart', 'hide');
            } else {
                chartRef.current.showChartPicker();
                Audit.LogUserAction('chart.show.chart.picker', 'chart', 'open');
            }
        } catch (error) {
            console.error('Error in chart picker:', error);
        }
    };

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                const result = await dataCharts.loadChart(props.chart_id);
                if (!isMounted) return;

                let drillOutput = DrillFunctions.GetDrillText(result.drills);
                setData(result.data);
                setDefinition(result.definition);
                setDrillText(drillOutput);
                setUpdatedText(DateFunctions.GenerateUpdateText(result.latest.latestDate));
                setShowUpdate(determineShowUpdate());
                setShowToolbar(determineShowToolbar());
                
                // Process the options first
                const processedOptions = {
                    ...result.options,
                    height: props.height || result.options.height || 0,
                    width: props.width || result.options.width || 0
                };
                
                // Set the processed options
                setOptions(processedOptions);
                
                // Initialize chart with processed options
                const newChart = new Chart(result.definition.chart_type, result.data, processedOptions);
                chartRef.current = newChart;
                setIsLoaded(true);
                
                Audit.LogUserAction('chart.render', 'chart', result.definition.chart_type);
            } catch (error) {
                console.error('Error loading chart:', error);
            }
        };

        fetchData();

        // Cleanup function
        return () => {
            isMounted = false;
            if (chartRef.current && typeof chartRef.current.destroy === 'function') {
                try {
                    chartRef.current.destroy();
                } catch (error) {
                    console.error('Error destroying chart:', error);
                }
                chartRef.current = null;
            }
        };
    }, [props.chart_id]);

    const determineShowUpdate = () => {
        return props.width >= 200;
    };

    const determineShowToolbar = () => {
        return props.width >= 200;
    };

    const getCardClasses = () => {
        return isLoaded ? "" : "";
    };

    return (
        <div className={getCardClasses()}>
            {isLoaded ? (
                <Paper 
                    elevation={3} 
                    sx={{ 
                        p: 2, 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <ChartHeader 
                        key={props.chart_id + 1} 
                        chartId={props.chart_id} 
                        showToolbar={showToolbar} 
                        displayName={definition.display_name} 
                        dashboardId={props.dashboard_id}
                        onChartPicker={loadChartPicker}
                        chartType={definition.chart_type}
                    />
                    <Graph key={props.chart_id} chart={chartRef.current} />
                    <ChartFooter key={props.chart_id + 100} showUpdate={showUpdate} drillText={drillText} updatedText={updatedText} />
                </Paper>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                </Box>
            )}
        </div>
    );
};

export default Card;